import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'

// 설정 모달 라우팅: 보던 화면 위에 모달로 열고 주소엔 ?setting=탭 으로 남긴다.
// 새로고침·링크로 같은 탭이 열리고, 뒤로가기는 이전 탭 → 닫기 순서로 돌아간다.
// 닫기는 이 세션에서 쌓은 설정 기록 수(history.state.settingDepth)만큼 한 번에 되돌린다.
// 링크·새로고침으로 바로 연 경우(쌓은 기록 없음)는 주소에서 setting 만 지운다(replace).

export const SETTING_QUERY_KEY = 'setting'
export const SETTING_TABS = [
  'item',
  'preset',
  'memo',
  'facility-image-memo',
  'config',
  'help',
] as const
export type SettingTab = (typeof SETTING_TABS)[number]

export function isSettingTab(value: unknown): value is SettingTab {
  return typeof value === 'string' && (SETTING_TABS as readonly string[]).includes(value)
}

// setting 을 뺀 쿼리 (마지막 경로 저장 등에서도 씀)
export function withoutSettingQuery(query: LocationQuery): LocationQuery {
  const rest = { ...query }
  delete rest[SETTING_QUERY_KEY]
  return rest
}

function settingDepth(): number {
  const depth = (window.history.state as Record<string, unknown> | null)?.settingDepth
  return typeof depth === 'number' && depth > 0 ? depth : 0
}

export function useSettingRoute(): {
  // 주소의 setting 값(없으면 null, 잘못된 값도 그대로 - 고치는 건 설정 화면 몫)
  rawSettingTab: ComputedRef<string | null>
  openSetting: (tab?: SettingTab) => void
  selectSettingTab: (tab: SettingTab) => void
  replaceSettingTab: (tab: SettingTab) => void
  closeSetting: () => void
} {
  const route = useRoute()
  const router = useRouter()

  const rawSettingTab = computed(() => {
    const value = route.query[SETTING_QUERY_KEY]
    const first = Array.isArray(value) ? value[0] : value
    return typeof first === 'string' ? first : null
  })

  function pushTab(tab: SettingTab, depth: number): void {
    router
      .push({ query: { ...route.query, [SETTING_QUERY_KEY]: tab }, state: { settingDepth: depth } })
      .catch(error => console.error('[Setting] 이동 실패', error))
  }

  function openSetting(tab: SettingTab = 'item'): void {
    if (rawSettingTab.value !== null) {
      selectSettingTab(tab)
      return
    }
    pushTab(tab, 1)
  }

  function selectSettingTab(tab: SettingTab): void {
    if (rawSettingTab.value === tab) return
    pushTab(tab, settingDepth() + 1)
  }

  // 기록을 쌓지 않고 탭만 바꾼다(잘못된 탭 보정)
  function replaceSettingTab(tab: SettingTab): void {
    router
      .replace({ query: { ...route.query, [SETTING_QUERY_KEY]: tab } })
      .catch(error => console.error('[Setting] 이동 실패', error))
  }

  function closeSetting(): void {
    if (rawSettingTab.value === null) return
    const depth = settingDepth()
    if (depth > 0) {
      router.go(-depth)
      return
    }
    router
      .replace({ query: withoutSettingQuery(route.query) })
      .catch(error => console.error('[Setting] 이동 실패', error))
  }

  return { rawSettingTab, openSetting, selectSettingTab, replaceSettingTab, closeSetting }
}
